import dataLayer from "./index";

const forms: Record<string, string> = {
  "DS-5504": "e0dfa980-f697-5b56-8ca3-2c91479e954b",
  "DS-64": "559d8b00-8315-50b3-9db3-0e363b8d9506",
  "DS-11": "59ce1679-1249-5ead-8c10-c0ee9731cfe8",
  "DS-82": "8684cd82-3522-514e-a646-2e164bce07b2",
  "DS-82-online": "0a1731ed-5277-11f0-823f-3ac73f8fb1c5",
};

interface Addon {
  id: string;
  name: string;
  amount: number;
}

export interface FormDataType {
  emailAddress: string;
  firstName: string;
  lastName: string;
  address1: string;
  city: string;
  stateTerritory: string;
  country: string;
  zipCode: string;
  phoneNumber1: string;
}

export const sendViewItemListEvent = (formNames: string[], price: number): void => {
  dataLayer.push("view_item_list", {
    ecommerce: {
      currency: "USD",
      view_item_list: {
        items: formNames.map((formName) => ({
          item_id: forms[formName],
          item_name: formName,
          item_category: "Form",
          price: price,
          quantity: 1,
        })),
      },
    },
  });
};

export const sendViewItemEvent = (formName: string, price: number): void => {
  dataLayer.push("view_item", {
    ecommerce: {
      view_item: {
        currency: "USD",
        value: price,
        items: {
          item_id: forms[formName],
          item_name: formName,
          item_category: "Form",
          price: price,
          quantity: 1,
        },
      },
    },
  });
};

export const sendAddToCartEvent = (
  formName: string,
  addons: Addon[] | undefined,
  price: number,
  totalPrice: number
): void => {
  const safeAddons = addons ?? [];
  let cartItems =
    safeAddons.length === 0
      ? []
      : safeAddons.map((addon) => ({
          item_id: addon.id,
          item_name: addon.name,
          item_category: "Addon",
          price: addon.amount,
          quantity: 1,
        }));

  cartItems.unshift({
    item_id: forms[formName],
    item_name: formName,
    item_category: "Form",
    price: price,
    quantity: 1,
  });

  dataLayer.push("add_to_cart", {
    ecommerce: {
      add_to_cart: {
        currency: "USD",
        value: totalPrice,
        items: cartItems,
      },
    },
  });
};

export const sendRemoveFromCartEvent = (addons: Addon | Addon[]): void => {
  const removedAddons = Array.isArray(addons) ? addons : [addons];
  const items = removedAddons.map((addon) => ({
    item_id: addon.id,
    item_name: addon.name,
    item_category: "Addon",
    price: addon.amount,
    quantity: 1,
  }));
  const value = removedAddons.reduce((sum, addon) => sum + addon.amount, 0);

  dataLayer.push("remove_from_cart", {
    ecommerce: {
      remove_from_cart: {
        currency: "USD",
        value: parseFloat(value.toFixed(2)),
        items,
      },
    },
  });
};

export const sendBeginCheckoutEvent = (
  formName: string,
  addons: Addon[] | undefined,
  price: number,
  totalPrice: number
): void => {
  const safeAddons = addons ?? [];
  let cartItems =
    safeAddons.length === 0
      ? []
      : safeAddons.map((addon) => ({
          item_id: addon.id,
          item_name: addon.name,
          item_category: "Addon",
          price: addon.amount,
          quantity: 1,
        }));

  cartItems.unshift({
    item_id: forms[formName],
    item_name: formName,
    item_category: "Form",
    price: price,
    quantity: 1,
  });

  dataLayer.push("begin_checkout", {
    ecommerce: {
      begin_checkout: {
        currency: "USD",
        value: totalPrice,
        items: cartItems,
      },
    },
  });
};

export const sendPurchaseEvent = (
  formName: string,
  orderReference: string,
  addons: Addon[] | undefined,
  price: number,
  totalPrice: number,
  formdata: FormDataType
): void => {
  const safeAddons = addons ?? [];
  let cartItems =
    safeAddons.length === 0
      ? []
      : safeAddons.map((addon) => ({
          item_id: addon.id,
          item_name: addon.name,
          item_category: "Addon",
          price: addon.amount,
          quantity: 1,
        }));

  cartItems.unshift({
    item_id: forms[formName],
    item_name: formName,
    item_category: "Form",
    price: price,
    quantity: 1,
  });

  const payload = {
    ecommerce: {
      purchase: {
        transaction_id: orderReference,
        value: totalPrice,
        currency: "USD",
        emailAddress: formdata?.emailAddress,
        firstName: formdata?.firstName,
        lastName: formdata?.lastName,
        street: formdata?.address1,
        city: formdata?.city,
        stateTerritory: formdata?.stateTerritory,
        country: formdata?.country,
        zipCode: formdata?.zipCode,
        phoneNumber: formdata?.phoneNumber1,
        items: cartItems,
      },
    },
  };

  dataLayer.push("purchase", payload);
};
